import { create } from 'zustand';
import type { DetectionKind } from '@/core/detection/types';
import { addManualTrack, createDefaultRecipe, removeManualTrack, type EditRecipe } from '@/core/project/recipe';
import { addCut, manualCut } from '@/core/project/timeline';
import { MIN_SPEED_SEC, setSpeed } from '@/core/project/speed';
import { applyPreset, presetFromRecipe, type Preset, type PresetSettings } from '@/core/project/preset';
import { arrange, layoutOf, type ArrangeContext } from '@/core/project/arrange';
import { createLayer, createTextLayer, duplicateLayer, layerAssetId, moveInStack, restack, textSizeFor, type Layer } from '@/core/project/layers';
import { DEFAULT_TEXT_STYLE, layoutText } from '@/core/project/text-layer';
import { measureText } from '@/engine/render/text-renderer';
import { selectCamera, selectKept, selectLayout } from './selectors';
import { usePlacesStore } from './places-store';
import { outputFileName as nameFor, withExtension, type OutputFormatId } from '@/core/project/output-format';
import { asConversion } from '@/core/project/convert';
import { applyPlacement, type Placement as PostPlacement } from '@/core/project/placement';
import { placeTimed } from '@/core/project/place-media';
import {
  assetTimeAt,
  insertClipsAt,
  keptAssetRanges,
  moveClip as moveClipIn,
  PRIMARY_ASSET,
  removeClip as removeClipFrom,
  splitClipAt,
  unplaceBox,
  updateClip,
  wholeClip,
  type AssetId,
  type Clip,
  type ClipFit,
  type SequenceLayout,
} from '@/core/project/sequence';
import type { Track } from '@/core/tracking/track';
import { engine, isCancelled, type Task } from '@/engine/client';
import { EngineError, toEngineError } from '@/engine/errors';
import type { AudioAnalysis, ExportJob, ExportResult, FilmstripFrame, MediaInfo, PrivacyScanResult, Progress, RegionTrackRequest, SoundInfo, TelemetryResult } from '@/engine/types';
import type { UnitSystem } from '@/core/telemetry/overlay';
import { scanRanges, type ScanPrecision } from '@/core/detection/scan-plan';
import type { ExtraOutputId } from '@/core/project/output-set';
import { fallbackSpeechLanguage } from '@/core/captions/language';
import { wordsToCues } from '@/core/captions/segment';
import { CAPTION_LANGUAGES, whisperTask, type AsrQuality, type CaptionLanguage, type CaptionTask, type CaptionWord } from '@/core/captions/types';
import { mayBeMetered } from '@/lib/data-cost';
import { readPreference, writePreference } from '@/lib/preferences';
import { keepsCaptionModel } from '@/engine/model-store';
import { rememberUnits, withPreferredUnits } from '@/lib/units';
import { range, type TimeRange } from '@/core/time/ranges';
import type { ScreenActivity } from '@/core/screen/activity';
import { notificationTracks, type NotificationHit } from '@/core/screen/notifications';
import { isSameVideo } from '@/core/media/same-video';
import { telemetrySources } from './intake';
import { restoredRecipe, type SavedProject } from './projects/project';
import { findPresetById } from './preset-store';
import { assetKindOf, assetLookup, byName, nextAssetId, nextItemId, readImageSize, type Asset, type AssetKind } from './assets';
import {
  addCoverage,
  DETECTION_KINDS,
  emptyCoverage,
  extendKindTracks,
  IDLE_SCAN,
  idleScans,
  isScanBusy,
  mapScans,
  replaceKindScan,
  replaceKindTracks,
  resetKindReveals,
  scannedKinds,
  settledScan,
  unscannedAssets,
  wholeCoverage,
  type Coverage,
  type KindScan,
} from './privacy-scans';
import { commitFrom, createHistory, pushHistory, redo, replacePresent, undo, type History } from './history';

export type LoadState =
  | { readonly status: 'empty' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready' }
  | { readonly status: 'error'; readonly error: EngineError };

export type AudioState =
  | { readonly status: 'idle' | 'running' }
  | { readonly status: 'done'; readonly analysis: AudioAnalysis | null }
  | { readonly status: 'error'; readonly error: EngineError };

/** What the background analyses found in one video (or sound). */
export interface AssetAnalysis {
  readonly audio: AudioState;
  /** Small frames for the timeline, in the asset's own time. */
  readonly filmstrip: readonly FilmstripFrame[];
}

export const IDLE_ANALYSIS: AssetAnalysis = { audio: { status: 'idle' }, filmstrip: [] };

export interface PrivacyScanState {
  /** One scan per kind, run one after the other (see `privacy-scans.ts`). */
  readonly scans: Readonly<Record<DetectionKind, KindScan>>;
  /** What was found in each video, in its own time (ids carry the video, see `assetTrackId`). */
  readonly tracksByAsset: Readonly<Record<AssetId, readonly Track[]>>;
  /** The parts of each video each kind has been looked for in (only what the edit shows gets scanned). */
  readonly covered: Coverage;
  /** Object URLs of track thumbnails. */
  readonly thumbnails: Readonly<Record<string, string>>;
  readonly precision: ScanPrecision;
  /** Kinds offered with one tap, not yet scanned. */
  readonly suggested: readonly DetectionKind[];
}

/**
 * Without a recipe that hides people, nothing is scanned unasked: finding faces
 * downloads a model and takes a while, so it is offered with one tap. A recipe
 * that hides them is the go-ahead, and starts its scans itself (`applyPreset`).
 */
export const SUGGESTED_SCAN_KINDS: readonly DetectionKind[] = ['face'];

/** Manually drawn areas: the drawing mode and the follow-up tracking task. */
export interface AreaState {
  readonly drawing: boolean;
  readonly status: 'idle' | 'running' | 'error';
  readonly progress: Progress | null;
  readonly error: EngineError | null;
  /** Object URLs of area thumbnails (kept across undo/redo). */
  readonly thumbnails: Readonly<Record<string, string>>;
}

/** Speech-to-text job and the user's model/language choice. */
export interface TranscriptionState {
  readonly status: 'idle' | 'running' | 'error';
  readonly progress: Progress | null;
  readonly error: EngineError | null;
  readonly quality: AsrQuality;
  readonly language: CaptionLanguage;
  /** Write the next captions as spoken or translated into English; as spoken unless asked (not remembered). */
  readonly task: CaptionTask;
  /** True once a transcription finished for this file (even if it found no speech). */
  readonly completed: boolean;
  /** A recipe or link asked for captions, but the model would come over mobile data: they wait for a tap. */
  readonly suggested: boolean;
  /** Language the current captions were written in (Whisper code), once known. */
  readonly spoken: string | null;
  /** Videos whose speech is in the captions. */
  readonly captioned: readonly AssetId[];
}

/** A screen recording's camera video, played and exported as its own layer. */
export interface CameraSource {
  readonly file: File;
  readonly url: string;
}

/** Ties an opened video to the recording session it came from (see `openFile`). */
export interface RecordingOrigin {
  /** The session's id on the device, so a saved project can keep it; null when it was only in memory. */
  readonly id: string | null;
  readonly camera: File | null;
  /** Takes thrown away while recording; the video opens with them cut (and can bring them back). */
  readonly cuts: readonly TimeRange[];
  /**
   * The editor is done with the recording: `keep` when it couldn't be opened (it
   * stays on the device to try again), otherwise it was left on purpose.
   */
  readonly release: (keep: boolean) => void;
  /** An export finished, so the recording made it out safely. */
  readonly exported: () => void;
}

/** Where a screen recording changes (for auto zoom) and the notifications found in it. */
export interface ScreenAnalysisState {
  readonly status: 'idle' | 'running' | 'done' | 'error';
  readonly progress: Progress | null;
  readonly activity: ScreenActivity | null;
  /** Notifications found (kept to keep the zoom away from them). */
  readonly notifications: readonly NotificationHit[];
}

/** A finished file, with an object URL to show and download it. */
export type SavedResult = ExportResult & { readonly url: string; readonly extra: ExtraOutputId | null };

/** One file to make from the edit: the main one, or an extra shape or kind of it (see `output-set.ts`). */
export interface ExportItem {
  readonly job: Omit<ExportJob, 'outputName' | 'camera' | 'cameraBackdrop' | 'sources'>;
  /** The file name the person chose (its extension is set from the format). */
  readonly name?: string;
  readonly extra?: ExtraOutputId | null;
}

/** Which of several files is being made now. */
export interface ExportBatch {
  readonly index: number;
  readonly total: number;
  readonly extra: ExtraOutputId | null;
  /** Width / height of the file being made, for the picture of it coming together. */
  readonly shape: number;
}

export interface ExportState {
  readonly status: 'idle' | 'configuring' | 'running' | 'done' | 'error';
  readonly progress: Progress | null;
  readonly startedAt: number | null;
  /** The main file (the first one made). */
  readonly result: SavedResult | null;
  /** Every file made this time, the main one first. */
  readonly results: readonly SavedResult[];
  /** Set when several files are made one after the other. */
  readonly batch: ExportBatch | null;
  /** What went wrong; with some files made already, the rest that couldn't be. */
  readonly error: EngineError | null;
  /** Object URL of the latest small picture of the frame being made, while it runs. */
  readonly preview: string | null;
}

/** What is chosen on the timeline or the preview, so its settings can be shown. */
export type Selection = { readonly kind: 'clip'; readonly id: string } | { readonly kind: 'layer'; readonly id: string };

/** Files being brought in, and the ones that couldn't be. */
export interface ImportState {
  readonly busy: number;
  readonly failed: readonly { readonly name: string; readonly error: EngineError | null }[];
}

/** Where an imported file goes in the edit. */
export type Placement = { readonly as: 'clip'; readonly at: number | 'end' } | { readonly as: 'layer'; readonly at: number };

interface EditorState {
  /** The opened video. */
  readonly file: File | null;
  readonly sourceUrl: string | null;
  /** The opened video's details; its frame is the edit's frame. */
  readonly media: MediaInfo | null;
  readonly load: LoadState;
  readonly history: History<EditRecipe>;
  /** Everything brought in, the opened video included (as `PRIMARY_ASSET`). */
  readonly assets: Readonly<Record<AssetId, Asset>>;
  /** Import order, for the media list. */
  readonly assetOrder: readonly AssetId[];
  readonly analyses: Readonly<Record<AssetId, AssetAnalysis>>;
  readonly importing: ImportState;
  readonly privacy: PrivacyScanState;
  readonly areas: AreaState;
  readonly transcription: TranscriptionState;
  readonly exporting: ExportState;
  /** Camera GPS data of the opened video; null until it has been looked for. */
  readonly telemetry: TelemetryResult | null;
  /** The presenter's camera of a screen recording, or null. */
  readonly camera: CameraSource | null;
  /** The person's own picture for an `image` camera background (kept out of the undo history). */
  readonly cameraBackdrop: CameraSource | null;
  readonly screen: ScreenAnalysisState;
  /** Set when the video was just recorded here; `exported` once it has been exported at least once. */
  readonly recording: { readonly exported: boolean } | null;
  /** The recipe the editor was opened with (offered again when handing videos to batch processing). */
  readonly openedWith: Preset | null;
  /** A link asked for captions (`?lang=`, `?task=translate`): the editor opens on them. */
  readonly captionsLinked: boolean;
  /** Current row time of the player (seconds), reported by the preview. */
  readonly playhead: number;
  /** Start of a part being marked for cutting by hand (row seconds), or null. */
  readonly cutMark: number | null;
  /** Start of a part being marked for speeding up (row seconds), or null. */
  readonly speedMark: number | null;
  /** The speed a newly marked part gets. */
  readonly speedChoice: number;
  readonly selection: Selection | null;
  /** A text layer just added, whose words are waiting to be typed over. */
  readonly freshText: string | null;
  /** The file name the person gave the export (without extension); null for the suggested one. */
  readonly exportName: string | null;
  /** How far the timeline is zoomed in (1 = the whole edit fits). */
  readonly timelineZoom: number;
  /** The saved project this edit is kept as (see `projects/autosave.ts`), once it is worth keeping, and when it began. */
  readonly projectId: string | null;
  readonly projectCreatedAt: number;
  /** The edit is kept on the device (written, or about to be where saving is known to work), so leaving loses nothing. */
  readonly projectSaved: boolean;
  /** The screen recording on the device the edit came from. */
  readonly recordingId: string | null;
}

/** How a video is opened (see `openFile`). */
export interface OpenOptions {
  readonly preset?: Preset;
  readonly recording?: RecordingOrigin;
  readonly restore?: Restore;
  readonly convertTo?: OutputFormatId;
  /** Keep the video under this many MB (`/?target=20mb`). */
  readonly sizeLimit?: number | null;
  /** Fit it for where it will be posted (`/?fit=tiktok`): frame, largest file, and its length checked from then on. */
  readonly placement?: PostPlacement | null;
  /** Captions a link asked for (`/?lang=tr&task=translate`): set up and offered, not started. */
  readonly captions?: { readonly language: CaptionLanguage | null; readonly task: CaptionTask | null } | null;
}

/** A saved project being opened again, with the files picked for it. */
export interface Restore {
  readonly project: SavedProject;
  readonly files: ReadonlyMap<AssetId, File>;
}

interface EditorActions {
  /**
   * Opens a video, optionally starting from a recipe chosen on the home page.
   * A screen recording passes its origin: the camera layer and lifecycle callbacks.
   * `convertTo` (quick convert, `/?to=mp4`) opens it as it is, straight into saving in that format;
   * `sizeLimit` starts it with "keep it under N MB" set; `placement` fits it for where it will be posted.
   */
  openFile(file: File, options?: OpenOptions): Promise<void>;
  /** Names the saved project this edit is kept as. */
  setProjectId(id: string, createdAt: number): void;
  setProjectSaved(saved: boolean): void;
  /** Opens the first of several videos (by name) and puts the others in the media list, ready to add. */
  openFiles(files: readonly File[], options?: Pick<OpenOptions, 'preset' | 'placement' | 'captions'>): Promise<void>;
  closeFile(): void;
  /**
   * Swaps in the same video picked again, keeping every edit, when an opened
   * file can no longer be read (a phone gallery or cloud sync changed it).
   * Work the lost file interrupted starts over. Resolves `different` — and
   * changes nothing — when `file` is none of the videos; rejects when it can't be opened.
   */
  relinkFile(file: File): Promise<'relinked' | 'different'>;
  /** Applies an edit as an undo step. */
  edit(update: (recipe: EditRecipe) => EditRecipe): void;
  /** Applies an edit without an undo step (continuous gestures)… */
  preview(update: (recipe: EditRecipe) => EditRecipe): void;
  /** …and records the whole gesture as one undo step, given the recipe from before it. */
  commit(base: EditRecipe): void;
  undo(): void;
  redo(): void;
  setPlayhead(time: number): void;
  /** Marks where a part to remove by hand starts… */
  startCut(time: number): void;
  /** …and removes everything between that mark and `time` (one undo step). */
  finishCut(time: number): void;
  cancelCut(): void;
  /** Marks where a part to speed up starts… */
  startSpeed(time: number): void;
  /** …and plays everything between that mark and `time` at the chosen speed (one undo step). */
  finishSpeed(time: number): void;
  cancelSpeed(): void;
  setSpeedChoice(rate: number): void;
  /** Sets how fast `span` plays (1 = normal again), as one undo step. */
  setSpeed(span: TimeRange, rate: number): void;
  select(selection: Selection | null): void;
  setTimelineZoom(zoom: number): void;
  /** Brings files into the media list (videos, pictures, sounds); resolves with the ids of the ones that worked. */
  importFiles(files: readonly File[]): Promise<AssetId[]>;
  /** Imports files and puts them in the edit. */
  importAndPlace(files: readonly File[], placement: Placement): Promise<void>;
  /** Puts an imported asset in the edit: a video as a clip, or anything as a layer on top. */
  place(assetId: AssetId, placement: Placement): void;
  /** Adds every video in the media list that isn't in the edit yet to its end. */
  appendUnused(): void;
  /** Takes an unused asset out of the media list. */
  removeAsset(assetId: AssetId): void;
  /** Forgets the files that couldn't be imported (the notice about them goes away). */
  dismissImportErrors(): void;
  /** Splits the clip under `time` in two (one undo step); false when too close to a clip edge. */
  splitAt(time: number): boolean;
  moveClip(id: string, index: number): void;
  removeClip(id: string): void;
  setClipFit(id: string, fit: ClipFit): void;
  updateLayer(id: string, update: (layer: Layer) => Layer): void;
  removeLayer(id: string): void;
  /** Moves a layer one step up (towards the front) or down. */
  restackLayer(id: string, direction: 1 | -1): void;
  /** Puts a layer at `index` of the stack (0 = bottom). */
  moveLayerTo(id: string, index: number): void;
  /** Puts a copy of a layer right above it and chooses the copy. */
  duplicateLayer(id: string): void;
  /** Puts words on top at the playhead and chooses them, ready to be typed over. */
  addTextLayer(text: string): void;
  /** Chooses a text layer and opens its words for typing. */
  editText(id: string): void;
  /** Forgets that a text layer was just added (its text field has taken focus). */
  clearFreshText(): void;
  setScanOptions(options: Partial<Pick<PrivacyScanState, 'precision'>>): void;
  /**
   * Queues scans for `kinds` (faces by default) in every video of the edit;
   * they run one after the other. By default every finding gets hidden
   * (privacy first); `revealAll` keeps them visible, e.g. when scanning only to reframe.
   */
  scanPrivacy(options?: { readonly kinds?: readonly DetectionKind[]; readonly revealAll?: boolean; readonly assets?: readonly AssetId[]; readonly mode?: ScanMode }): void;
  /**
   * Scans what the edit shows that earlier scans didn't look at (a cut undone,
   * a clip made longer, a video added), for every kind already scanned.
   * Runs by itself shortly after editing pauses, and before saving.
   */
  scanUnscanned(): void;
  /** Stops the scans of `kind` (running or waiting); earlier results of that kind stay. */
  cancelScan(kind: DetectionKind): void;
  /** Forgets everything found of `kind`, so nothing of it is hidden any more. */
  clearScan(kind: DetectionKind): void;
  setDrawingArea(drawing: boolean): void;
  /** Follows an area drawn at row time `request.time` (box in the frame) through its video. */
  addArea(request: RegionTrackRequest): Promise<void>;
  cancelAreaTracking(): void;
  removeArea(trackId: string): void;
  setTranscriptionOptions(options: Partial<Pick<TranscriptionState, 'quality' | 'language' | 'task'>>): void;
  /** Writes down what is said in the edit's videos; `onlyMissing` adds only the videos without captions yet. */
  /** `burnIn: false` leaves burning the captions in as it is (the words are wanted for editing, not on screen). */
  transcribe(options?: { readonly onlyMissing?: boolean; readonly burnIn?: boolean }): Promise<void>;
  cancelTranscription(): void;
  /** Finds where the screen changes (auto zoom) and hides notifications that popped up. */
  analyzeScreen(): Promise<void>;
  /** Switches km/h ↔ mph for this video and remembers the choice. */
  setUnits(units: UnitSystem): void;
  /**
   * Applies a preset to the current video. Choosing a recipe is the go-ahead
   * for what it does: its cuts apply, and its scans and captions start (only
   * captions wait for a tap where their model would come over mobile data).
   * `initial` applies it as the starting point rather than as an undoable edit.
   */
  applyPreset(preset: Preset, options?: { readonly initial?: boolean }): void;
  /** Current settings as a reusable preset. */
  currentPresetSettings(): PresetSettings;
  openExport(): void;
  /** Remembers the file name typed for the export (left empty, the suggested one is used). */
  setExportName(name: string): void;
  /** `name` is the file name the person chose (its extension is set from the format). */
  startExport(job: ExportItem['job'], options?: { readonly name?: string }): Promise<void>;
  /**
   * Makes several files from the edit one after the other (the main one first).
   * Stopped or failing part way, the files already made stay ready to save.
   */
  startExports(items: readonly ExportItem[]): Promise<void>;
  /** Sets (or clears) the image behind the person for an `image` camera background. */
  setCameraBackdrop(file: File | null): void;
  cancelExport(): void;
  closeExport(): void;
}

export type EditorStore = EditorState & EditorActions;

const initialPrecision = (readPreference('privacy-precision') === 'thorough' ? 'thorough' : 'fast') satisfies ScanPrecision;

const idlePrivacy = (): PrivacyScanState => ({
  scans: idleScans(),
  tracksByAsset: {},
  covered: emptyCoverage(),
  thumbnails: {},
  precision: initialPrecision,
  suggested: SUGGESTED_SCAN_KINDS,
});

const idleAreas: AreaState = { drawing: false, status: 'idle', progress: null, error: null, thumbnails: {} };

const storedLanguage = readPreference('caption-language');
const idleTranscription = (): TranscriptionState => ({
  status: 'idle',
  progress: null,
  error: null,
  completed: false,
  suggested: false,
  task: 'transcribe',
  spoken: null,
  captioned: [],
  quality: readPreference('caption-quality') === 'fast' ? 'fast' : 'accurate',
  language: CAPTION_LANGUAGES.includes(storedLanguage as CaptionLanguage) ? (storedLanguage as CaptionLanguage) : 'auto',
});

const idleExport: ExportState = { status: 'idle', progress: null, startedAt: null, result: null, results: [], batch: null, error: null, preview: null };

/** Keeps the files of the last export when the dialog changes state. */
const keptResults = (exporting: ExportState): Pick<ExportState, 'result' | 'results'> => ({ result: exporting.result, results: exporting.results });

const initialState = (): EditorState => ({
  file: null,
  sourceUrl: null,
  media: null,
  load: { status: 'empty' },
  history: createHistory(withPreferredUnits(createDefaultRecipe())),
  assets: {},
  assetOrder: [],
  analyses: {},
  importing: { busy: 0, failed: [] },
  privacy: idlePrivacy(),
  areas: idleAreas,
  transcription: idleTranscription(),
  exporting: idleExport,
  telemetry: null,
  camera: null,
  cameraBackdrop: null,
  screen: { status: 'idle', progress: null, activity: null, notifications: [] },
  recording: null,
  openedWith: null,
  captionsLinked: false,
  playhead: 0,
  cutMark: null,
  speedMark: null,
  speedChoice: 2,
  selection: null,
  freshText: null,
  exportName: null,
  timelineZoom: 1,
  projectId: null,
  projectCreatedAt: 0,
  projectSaved: false,
  recordingId: null,
});

/** Long-running tasks live outside of state: they are not data, just handles. Keyed by job (and asset). */
const tasks = new Map<string, Task<unknown>>();

/** Every editing session gets a new number; results of an earlier one are dropped. */
let session = 0;

/** Lifecycle callbacks of the recording being edited (functions, so not state). */
let origin: RecordingOrigin | null = null;

/**
 * `replace` looks again at everything the edit shows and replaces what earlier
 * scans found; `extend` only looks at the parts no scan has seen and adds to it.
 */
export type ScanMode = 'replace' | 'extend';

/** Privacy scans waiting for the one running to finish, one per kind and video. */
interface ScanJob {
  readonly kind: DetectionKind;
  readonly assetId: AssetId;
  readonly revealAll: boolean;
  readonly mode: ScanMode;
}
let scanQueue: ScanJob[] = [];
let scanning: ScanJob | null = null;
/**
 * Per kind: how many videos the current round scans, how many are done (one
 * progress bar for all), and the first failure: one video that can't be
 * scanned doesn't stop the others.
 */
interface ScanRound {
  total: number;
  done: number;
  error: EngineError | null;
}
const freshRound = (): ScanRound => ({ total: 0, done: 0, error: null });
const scanRounds: Record<DetectionKind, ScanRound> = { face: freshRound(), plate: freshRound() };

/** Parts brought back into the edit get scanned once editing pauses for this long. */
const COVERAGE_CHECK_DELAY_MS = 1200;
let coverageTimer: ReturnType<typeof setTimeout> | null = null;

function cancelCoverageCheck(): void {
  if (coverageTimer !== null) clearTimeout(coverageTimer);
  coverageTimer = null;
}

function cancelAll(): void {
  cancelCoverageCheck();
  scanQueue = [];
  scanning = null;
  for (const kind of DETECTION_KINDS) scanRounds[kind] = freshRound();
  for (const task of tasks.values()) task.cancel();
}

/** Cancels every task and waits until each one has reported back, so no state is left mid-way. */
async function settleAll(): Promise<void> {
  const pending = [...tasks.values()].map((task) => task.promise);
  cancelAll();
  await Promise.allSettled(pending);
}

/** Lets go of the current recording session, if the editor holds one. */
function releaseOrigin(keep = false): void {
  const current = origin;
  origin = null;
  current?.release(keep);
}

/** Told about the edit just before it is closed, while its pictures and frames can still be read. */
const leaveListeners = new Set<(state: EditorStore) => void>();

/** Runs `listener` whenever the open edit is about to be closed (or replaced by another file); returns the unsubscribe. */
export function onLeaveEdit(listener: (state: EditorStore) => void): () => void {
  leaveListeners.add(listener);
  return () => void leaveListeners.delete(listener);
}

function revokeAll(state: EditorState): void {
  for (const asset of Object.values(state.assets)) URL.revokeObjectURL(asset.url);
  if (state.camera) URL.revokeObjectURL(state.camera.url);
  if (state.cameraBackdrop) URL.revokeObjectURL(state.cameraBackdrop.url);
  for (const url of Object.values(state.privacy.thumbnails)) URL.revokeObjectURL(url);
  for (const url of Object.values(state.areas.thumbnails)) URL.revokeObjectURL(url);
  for (const result of state.exporting.results) URL.revokeObjectURL(result.url);
  if (state.exporting.preview) URL.revokeObjectURL(state.exporting.preview);
  for (const analysis of Object.values(state.analyses)) for (const frame of analysis.filmstrip) frame.bitmap.close();
}

/** The suggested name of the saved file, from the opened video's name. */
export const outputFileName = nameFor;

/** What a change of the row needs to know besides the recipe. */
export function arrangeContext(state: Pick<EditorState, 'assets' | 'media'>): ArrangeContext {
  const media = state.media;
  return {
    lookup: (id) => {
      const asset = state.assets[id];
      return asset?.kind === 'video' ? asset : undefined;
    },
    frame: media ? { width: media.width, height: media.height } : { width: 16, height: 9 },
    primary: media ?? undefined,
  };
}

/** The row of clips the edit plays now. */
export const rowOf = (state: Pick<EditorState, 'assets' | 'media' | 'history'>): SequenceLayout => layoutOf(state.history.present, arrangeContext(state));

/** Videos that are in the edit, in row order without repeats. */
export const rowAssets = (state: Pick<EditorState, 'assets' | 'media' | 'history'>): Asset[] =>
  [...new Set(rowOf(state).clips.map((clip) => clip.assetId))].map((id) => state.assets[id]).filter((asset): asset is Asset => Boolean(asset));

/** The parts of each video the finished edit shows (its own seconds): all a privacy scan needs to look at. */
export function wantedParts(state: Pick<EditorState, 'assets' | 'media' | 'history' | 'analyses' | 'telemetry'>): (assetId: AssetId) => TimeRange[] {
  const { media } = state;
  if (!media) return () => [];
  const { row, keep } = selectKept({
    media,
    recipe: state.history.present,
    soundOf: (id) => {
      const audio = state.analyses[id]?.audio;
      return audio?.status === 'done' ? audio.analysis : null;
    },
    telemetry: state.telemetry?.status === 'ok' ? state.telemetry.telemetry : null,
    places: usePlacesStore.getState().places,
    assets: assetLookup(state.assets),
  });
  return (assetId) => keptAssetRanges(row, keep, assetId);
}

function primaryAsset(file: File, url: string, media: MediaInfo): Asset {
  return { id: PRIMARY_ASSET, kind: 'video', file, url, width: media.width, height: media.height, durationSec: media.durationSec, video: media, sound: null, hasAudio: media.audio?.decodable ?? false, removed: false };
}

/** Reads what an imported file is; rejects with an engine error when it can't be used. */
async function readAsset(file: File, kind: AssetKind): Promise<Omit<Asset, 'id' | 'url' | 'removed'>> {
  if (kind === 'video') {
    const media = (await engine.probe(file).promise) as MediaInfo;
    return { kind, file, width: media.width, height: media.height, durationSec: media.durationSec, video: media, sound: null, hasAudio: media.audio?.decodable ?? false };
  }
  if (kind === 'audio') {
    const sound = (await engine.probeSound(file).promise) as SoundInfo;
    if (!(sound.durationSec > 0)) throw new EngineError('no-audio');
    return { kind, file, width: 0, height: 0, durationSec: sound.durationSec, video: null, sound, hasAudio: true };
  }
  const size = await readImageSize(file).catch(() => {
    throw new EngineError('unsupported-format', file.name);
  });
  return { kind, file, ...size, durationSec: 0, video: null, sound: null, hasAudio: false };
}

export const useEditorStore = create<EditorStore>()((set, get) => {
  const toError = (error: unknown) => toEngineError(error);

  const setScan = (kind: DetectionKind, update: (scan: KindScan) => KindScan) =>
    set((state) => ({ privacy: { ...state.privacy, scans: { ...state.privacy.scans, [kind]: update(state.privacy.scans[kind]) } } }));

  const setAnalysis = (assetId: AssetId, update: (analysis: AssetAnalysis) => AssetAnalysis) =>
    set((state) => ({ analyses: { ...state.analyses, [assetId]: update(state.analyses[assetId] ?? IDLE_ANALYSIS) } }));

  /** Runs a task under `key`, dropping its result if the session ended meanwhile. */
  function track<T>(key: string, task: Task<T>): Task<T> {
    tasks.set(key, task as Task<unknown>);
    void task.promise.finally(() => tasks.get(key) === (task as Task<unknown>) && tasks.delete(key)).catch(() => undefined);
    return task;
  }

  /** One progress bar for a kind scanned in several videos one after the other. */
  function roundProgress(kind: DetectionKind, progress: Progress): Progress {
    const round = scanRounds[kind];
    if (round.total <= 1 || (progress.phase === 'model' && round.done === 0)) return progress;
    const ratio = progress.phase === 'scan' ? progress.ratio : 0;
    return { ...progress, phase: 'scan', ratio: Math.min(1, (round.done + ratio) / round.total) };
  }

  /** Starts the next queued privacy scan unless one is running. */
  async function runNextScan(token: number): Promise<void> {
    if (scanning || token !== session) return;
    const next = scanQueue.shift();
    if (!next) return;
    const { kind, assetId, revealAll, mode } = next;
    const state = get();
    const asset = state.assets[assetId];
    if (!asset) return void runNextScan(token);
    // Decided now rather than when queued: the edit may have changed while it waited.
    const covered = mode === 'extend' ? (state.privacy.covered[kind][assetId] ?? []) : [];
    const ranges = scanRanges(wantedParts(state)(assetId), covered, range(0, asset.durationSec));
    scanning = next;
    let succeeded = false;
    try {
      let result: PrivacyScanResult = { tracks: [], thumbnails: {} };
      // Nothing left to look at (all of it cut, or already scanned): settled without starting the engine.
      if (ranges.length > 0) {
        setScan(kind, (scan) => ({ ...scan, status: 'running', progress: roundProgress(kind, { phase: 'model', ratio: 0 }), error: null, partial: mode === 'extend' }));
        const task = track(
          'scan',
          engine.scanPrivacy(asset.file, { kinds: [kind], precision: get().privacy.precision, ranges }, (progress) => {
            if (token === session) setScan(kind, (scan) => ({ ...scan, progress: roundProgress(kind, progress) }));
          }),
        );
        result = await task.promise;
      }
      if (token !== session) return;
      const previous = get().privacy;
      const own = previous.tracksByAsset[assetId] ?? [];
      const merged = mode === 'extend' ? extendKindTracks(own, kind, result.tracks, assetId) : replaceKindScan(own, kind, result.tracks, assetId);
      const ownIds = new Set(own.map((item) => item.id));
      const added = [...new Set(merged.ids.values())].filter((id) => !ownIds.has(id));
      // A track the new scan only continued keeps the picture it already had.
      const replaced = mode === 'replace' ? own.filter((item) => item.kind === kind).map((item) => item.id) : [];
      for (const id of replaced) if (previous.thumbnails[id]) URL.revokeObjectURL(previous.thumbnails[id]);
      const thumbnails = Object.fromEntries(
        Object.entries(result.thumbnails).flatMap(([id, blob]) => {
          const final = merged.ids.get(id);
          return final && (mode === 'replace' || !ownIds.has(final)) ? [[final, URL.createObjectURL(blob)]] : [];
        }),
      );
      const more = scanQueue.some((job) => job.kind === kind);
      set((current) => {
        const present = current.history.present;
        const reveal = revealAll ? added : [];
        const privacy =
          mode === 'replace'
            ? { ...resetKindReveals(present.privacy, kind, reveal, assetId), enabled: revealAll ? present.privacy.enabled : true }
            : { ...present.privacy, revealedTrackIds: [...present.privacy.revealedTrackIds, ...reveal] };
        return {
          privacy: {
            ...current.privacy,
            scans: {
              ...current.privacy.scans,
              [kind]: { status: more ? 'running' : 'done', progress: more ? current.privacy.scans[kind].progress : null, error: scanRounds[kind].error, completed: true, partial: more && current.privacy.scans[kind].partial },
            },
            tracksByAsset: { ...current.privacy.tracksByAsset, [assetId]: merged.tracks },
            covered: addCoverage(current.privacy.covered, kind, assetId, ranges, mode === 'replace'),
            thumbnails: { ...Object.fromEntries(Object.entries(current.privacy.thumbnails).filter(([id]) => !replaced.includes(id))), ...thumbnails },
            suggested: current.privacy.suggested.filter((suggested) => suggested !== kind),
          },
          // Not an undo step: it is what was found, there is nothing meaningful to undo back to.
          history: replacePresent(current.history, { ...present, privacy }),
        };
      });
      succeeded = true;
    } catch (error) {
      if (token !== session) return;
      if (isCancelled(error)) setScan(kind, settledScan);
      else {
        // The other videos still get scanned; what this one couldn't be is said once the round ends.
        const failure = toError(error);
        scanRounds[kind].error ??= failure;
        const more = scanQueue.some((job) => job.kind === kind);
        setScan(kind, (scan) => (more ? scan : { ...scan, status: scan.completed ? 'done' : 'error', progress: null, error: scanRounds[kind].error, partial: false }));
      }
    } finally {
      if (token === session) {
        scanning = null;
        scanRounds[kind].done++;
        if (!scanQueue.some((job) => job.kind === kind)) scanRounds[kind] = freshRound();
        // The edit may have changed while this one ran: what it brought back is looked at next.
        if (succeeded && scanQueue.length === 0) scheduleCoverageCheck();
        void runNextScan(token);
      }
    }
  }

  /** Starts the background analyses that enrich the editor for one video; none of them block editing. */
  function analyzeAsset(asset: Asset, token = session): void {
    if (asset.hasAudio) analyzeAudioOf(asset, token);
    else setAnalysis(asset.id, (analysis) => ({ ...analysis, audio: { status: 'done', analysis: null } }));
    if (asset.kind === 'video') loadFilmstrip(asset, token);
  }

  function analyzeAudioOf(asset: Asset, token: number): void {
    setAnalysis(asset.id, (analysis) => ({ ...analysis, audio: { status: 'running' } }));
    const task = track(`audio:${asset.id}`, engine.analyzeAudio(asset.file));
    task.promise
      .then((analysis) => token === session && setAnalysis(asset.id, (current) => ({ ...current, audio: { status: 'done', analysis: analysis as AudioAnalysis | null } })))
      .catch((error: unknown) => !isCancelled(error) && token === session && setAnalysis(asset.id, (current) => ({ ...current, audio: { status: 'error', error: toError(error) } })));
  }

  function loadFilmstrip(asset: Asset, token: number): void {
    // Imported videos only need a few frames until they are placed; the opened one gets the full strip.
    const count = asset.id === PRIMARY_ASSET ? 24 : Math.min(24, Math.max(6, Math.round(asset.durationSec / 5)));
    const task = track(`filmstrip:${asset.id}`, engine.filmstrip(asset.file, count, 96));
    task.promise
      .then((frames) => {
        const list = frames as FilmstripFrame[];
        if (token === session && get().assets[asset.id]?.file === asset.file) setAnalysis(asset.id, (current) => ({ ...current, filmstrip: list }));
        else list.forEach((frame) => frame.bitmap.close());
      })
      .catch(() => undefined); // The filmstrip is decorative; the editor works without it.
  }

  function readTelemetryOf(file: File, token: number): void {
    // Reading the GPS track is fast and only adds an optional gauge, so a failure just means "no data".
    const task = track('telemetry', engine.readTelemetry(telemetrySources(file)));
    task.promise
      .then((result) => token === session && set({ telemetry: result as TelemetryResult }))
      .catch((error: unknown) => !isCancelled(error) && token === session && set({ telemetry: { status: 'none' } }));
  }

  /** A video that just joined the edit gets the same privacy scans as the others. */
  function catchUpScans(assetIds: readonly AssetId[]): void {
    const { scans } = get().privacy;
    const kinds = DETECTION_KINDS.filter((kind) => scans[kind].completed || isScanBusy(scans[kind]));
    if (kinds.length > 0 && assetIds.length > 0) get().scanPrivacy({ kinds, assets: assetIds, mode: 'extend' });
  }

  /** A video that joins the edit gets its sound analyzed and its frames read, once. */
  function ensureAnalyzed(asset: Asset): void {
    const analysis = get().analyses[asset.id];
    if (!analysis || analysis.audio.status === 'idle') {
      if (asset.hasAudio) analyzeAudioOf(asset, session);
      else setAnalysis(asset.id, (current) => ({ ...current, audio: { status: 'done', analysis: null } }));
    }
    if (asset.kind === 'video' && !analysis?.filmstrip.length && !tasks.has(`filmstrip:${asset.id}`)) loadFilmstrip(asset, session);
  }

  /** Applies a new row of clips as one undo step, moving everything made on it along. */
  function arrangeRow(clips: readonly Clip[]): void {
    const state = get();
    const before = new Set(rowOf(state).clips.map((clip) => clip.assetId));
    state.edit((recipe) => arrange(recipe, clips, arrangeContext(state)));
    const joined = [...new Set(clips.map((clip) => clip.assetId))].filter((id) => !before.has(id));
    for (const id of joined) {
      const asset = get().assets[id];
      if (asset) ensureAnalyzed(asset);
    }
    catchUpScans(joined);
  }

  /**
   * Puts a saved project back in place over the opened video: its edit, the
   * other files it uses, what was found in them and the captions' state.
   * Sound and frames are analyzed again (quick); scans and captions are not.
   */
  function restoreProject({ project, files }: Restore, token: number): void {
    const state = get();
    const assets: Record<AssetId, Asset> = { ...state.assets };
    for (const saved of project.assets) {
      const file = files.get(saved.id);
      if (saved.id === PRIMARY_ASSET || !file) continue;
      const { id, kind, width, height, durationSec, video, sound, hasAudio, removed } = saved;
      assets[id] = { id, kind, file, url: URL.createObjectURL(file), width, height, durationSec, video, sound, hasAudio, removed };
    }
    const urls = (blobs: Readonly<Record<string, Blob>>) => Object.fromEntries(Object.entries(blobs).map(([id, blob]) => [id, URL.createObjectURL(blob)]));
    const completed = new Set(project.privacy.completed);
    const preset = project.presetId ? findPresetById(project.presetId) : null;
    set({
      history: createHistory(restoredRecipe(project)),
      assets,
      assetOrder: project.assetOrder.filter((id) => assets[id]),
      openedWith: preset,
      privacy: {
        ...state.privacy,
        tracksByAsset: project.privacy.tracksByAsset,
        // Projects saved before only parts were scanned looked at every video whole.
        covered: project.privacy.covered ?? wholeCoverage(project.privacy.scanned, (id) => (id === PRIMARY_ASSET ? state.media?.durationSec : assets[id]?.durationSec)),
        scans: mapScans(state.privacy.scans, (kind, scan) => (completed.has(kind) ? { ...scan, status: 'done', completed: true } : scan)),
        thumbnails: urls(project.privacy.thumbnails),
        precision: project.privacy.precision,
        suggested: state.privacy.suggested.filter((kind) => !completed.has(kind)),
      },
      areas: { ...state.areas, thumbnails: urls(project.areaThumbnails) },
      transcription: { ...state.transcription, ...project.transcription, status: 'idle', progress: null, error: null, suggested: false },
    });
    const row = rowOf(get());
    for (const id of new Set(row.clips.map((clip) => clip.assetId))) {
      const asset = get().assets[id];
      if (asset && id !== PRIMARY_ASSET) ensureAnalyzed(asset);
    }
    for (const asset of Object.values(get().assets)) {
      if (asset.kind === 'audio') analyzeAsset(asset, token);
      else if (asset.kind === 'video' && asset.id !== PRIMARY_ASSET && !get().analyses[asset.id]) loadFilmstrip(asset, token);
    }
  }

  /** Makes one file, showing its frames as they come; resolves with it ready to save. */
  async function exportOne({ job, name, extra = null }: ExportItem, token: number): Promise<SavedResult> {
    const { file, camera, cameraBackdrop, assets } = get();
    const withCamera = camera && job.recipe.camera.layout !== 'off' ? camera.file : null;
    const backdrop = withCamera && job.recipe.camera.background.mode === 'image' ? (cameraBackdrop?.file ?? null) : null;
    // Only the files the edit uses go to the exporter.
    const used = new Set([...job.clips.map((clip) => clip.assetId), ...job.layers.flatMap((resolved) => layerAssetId(resolved.layer) ?? [])]);
    const sources = Object.fromEntries([...used].flatMap((id) => (assets[id] ? [[id, assets[id].file]] : [])));
    const format = job.recipe.output;
    const outputName = withExtension(name?.trim() || outputFileName(file!.name, format), format);
    const showPreview = (picture: Blob) => {
      const url = URL.createObjectURL(picture);
      set((state) => {
        if (state.exporting.status !== 'running') {
          URL.revokeObjectURL(url);
          return state;
        }
        if (state.exporting.preview) URL.revokeObjectURL(state.exporting.preview);
        return { exporting: { ...state.exporting, preview: url } };
      });
    };
    const task = track(
      'export',
      engine.exportVideo({ ...job, sources, camera: withCamera, cameraBackdrop: backdrop, outputName, livePreview: true }, (progress) => {
        if (token !== session) return;
        if (progress.preview) showPreview(progress.preview);
        const { preview: _preview, ...rest } = progress;
        set((state) => ({ exporting: { ...state.exporting, progress: rest } }));
      }),
    );
    try {
      const result = await task.promise;
      return { ...result, url: URL.createObjectURL(result.file), extra };
    } finally {
      // Each file starts from its own first frame, not the last one of the file before.
      const preview = get().exporting.preview;
      if (preview && token === session) {
        URL.revokeObjectURL(preview);
        set((state) => ({ exporting: { ...state.exporting, preview: null } }));
      }
    }
  }

  /**
   * Starts the captions a recipe or link asked for. The speech model is large
   * (100–300 MB), so where it would come over mobile data and isn't on the
   * device yet, they wait for a tap instead (the Captions tab says why).
   */
  async function autoTranscribe(token: number, { burnIn = true }: { readonly burnIn?: boolean } = {}): Promise<void> {
    // After the rest of the step that asked: a link's caption language is set after its recipe's (see `openFile`).
    await Promise.resolve();
    if (token !== session) return;
    const { media, transcription } = get();
    if (!media?.audio?.decodable || transcription.status === 'running') return;
    const free = !mayBeMetered() || (await keepsCaptionModel(transcription.quality).catch(() => false));
    if (token !== session) return;
    if (free) void get().transcribe({ burnIn });
    else set((state) => ({ transcription: { ...state.transcription, suggested: true } }));
  }

  const currentClips = (): Clip[] => rowOf(get()).clips.map(({ id, assetId, in: start, out, fit }) => ({ id, assetId, in: start, out, fit }));

  return {
    ...initialState(),

    async openFile(file, { preset, recording, restore, convertTo, sizeLimit, placement, captions } = {}) {
      if (get().load.status === 'ready') leaveListeners.forEach((listener) => listener(get()));
      cancelAll();
      revokeAll(get());
      releaseOrigin(get().projectId !== null);
      origin = recording ?? null;
      const token = ++session;
      set({
        ...initialState(),
        file,
        load: { status: 'loading' },
        recording: recording ? { exported: false } : null,
        openedWith: preset ?? null,
        recordingId: recording?.id ?? null,
        projectId: restore?.project.id ?? null,
        projectCreatedAt: restore?.project.createdAt ?? 0,
        projectSaved: restore !== undefined,
      });
      const probe = track('probe', engine.probe(file));
      // The camera is optional: if it can't be read, the recording still opens without it.
      const cameraProbe = recording?.camera ? track('camera', engine.probe(recording.camera)) : null;
      const camera = cameraProbe?.promise.then(() => recording!.camera!).catch(() => null) ?? Promise.resolve(null);
      try {
        const media = (await probe.promise) as MediaInfo;
        const cameraFile = await camera;
        if (token !== session) return;
        const url = URL.createObjectURL(file);
        const asset = primaryAsset(file, url, media);
        set({
          media,
          load: { status: 'ready' },
          sourceUrl: url,
          assets: { [PRIMARY_ASSET]: asset },
          assetOrder: [PRIMARY_ASSET],
          camera: cameraFile && { file: cameraFile, url: URL.createObjectURL(cameraFile) },
        });
        analyzeAsset(asset, token);
        readTelemetryOf(file, token);
        if (restore) {
          restoreProject(restore, token);
          if (recording || get().history.present.zoom.enabled) void get().analyzeScreen();
          return;
        }
        if (convertTo) {
          // The conversion is the starting point, not an edit to undo; the editor stays behind the dialog for anyone who wants to change more.
          set((state) => ({ history: createHistory({ ...asConversion(state.history.present, convertTo), sizeLimit: sizeLimit ?? null }) }));
          get().openExport();
          return;
        }
        if (preset?.settings.privacy.scan.length) get().setScanOptions({ precision: preset.settings.privacy.precision });
        // Privacy is the product, but scanning is the person's call: the Privacy tab
        // (open first) offers finding faces with one tap.
        if (preset) get().applyPreset(preset, { initial: true });
        if (sizeLimit) set((state) => ({ history: createHistory({ ...state.history.present, sizeLimit }) }));
        // After the recipe, so the place's frame wins over the recipe's; like the recipe, a starting point rather than an edit to undo.
        if (placement) set((state) => ({ history: createHistory(applyPlacement(state.history.present, placement, media)) }));
        // After the recipe too, whose own caption language it overrides; a link is not a preference, so nothing is remembered.
        if (captions && media.audio?.decodable)
          set((state) => ({
            captionsLinked: true,
            // Translating needs the larger model: the small one translates several times better (Whisper paper, Table 14).
            transcription: {
              ...state.transcription,
              ...(captions.language && { language: captions.language }),
              ...(captions.task && { task: captions.task }),
              ...(captions.task === 'translate' && { quality: 'accurate' as const }),
            },
          }));
        // Like a recipe's captions, a captions page's link is the go-ahead to write them.
        if (captions) void autoTranscribe(token);
        // Retakes are the starting point, not an edit to undo.
        if (recording?.cuts.length) set((state) => ({ history: createHistory({ ...state.history.present, cuts: recording.cuts }) }));
        // Screen recordings get their zoom moments and notification check right away.
        if (recording || get().history.present.zoom.enabled) void get().analyzeScreen();
      } catch (error) {
        if (isCancelled(error) || token !== session) return;
        releaseOrigin(true);
        set({ load: { status: 'error', error: toError(error) } });
      }
    },

    async openFiles(files, options) {
      const [first, ...rest] = byName(files);
      if (!first) return;
      await get().openFile(first, options);
      if (rest.length > 0 && get().load.status === 'ready') await get().importFiles(rest);
    },

    async relinkFile(next) {
      const state = get();
      if (!state.media) return 'different';
      const candidate = (await engine.probe(next).promise) as MediaInfo;
      const target = Object.values(get().assets).find((asset) => asset.video && isSameVideo(asset.video, candidate));
      if (!target) return 'different';
      // Let interrupted work settle against the old file first, so it is known what to start again.
      const interrupted = DETECTION_KINDS.filter((kind) => ['queued', 'running', 'error'].includes(get().privacy.scans[kind].status));
      const token = session;
      await settleAll();
      if (token !== session) return 'different';
      const current = get();
      const old = current.assets[target.id]!;
      URL.revokeObjectURL(old.url);
      const relinked: Asset = { ...old, file: next, url: URL.createObjectURL(next) };
      set({
        ...(target.id === PRIMARY_ASSET ? { file: next, sourceUrl: relinked.url } : {}),
        assets: { ...current.assets, [target.id]: relinked },
        privacy: { ...current.privacy, scans: mapScans(current.privacy.scans, (kind, scan) => (interrupted.includes(kind) ? settledScan(scan) : scan)) },
      });
      // Everything was stopped to swap the file: unfinished analyses of every video start again.
      for (const asset of Object.values(get().assets)) {
        const analysis = get().analyses[asset.id];
        if (!analysis) continue;
        if (asset.hasAudio && (analysis.audio.status === 'running' || analysis.audio.status === 'error' || analysis.audio.status === 'idle')) analyzeAudioOf(asset, token);
        if (asset.kind === 'video' && analysis.filmstrip.length === 0) loadFilmstrip(asset, token);
      }
      if (current.telemetry === null && current.file) readTelemetryOf(current.file, token);
      if (interrupted.length > 0) get().scanPrivacy({ kinds: interrupted });
      return 'relinked';
    },

    closeFile() {
      if (get().load.status === 'ready') leaveListeners.forEach((listener) => listener(get()));
      cancelAll();
      revokeAll(get());
      // A project keeps its recording on the device; otherwise leaving lets it go.
      releaseOrigin(get().projectId !== null);
      session++;
      set(initialState());
    },

    setProjectId(projectId, projectCreatedAt) {
      set({ projectId, projectCreatedAt });
    },

    setProjectSaved(projectSaved) {
      if (get().projectSaved !== projectSaved) set({ projectSaved });
    },

    edit(update) {
      set((state) => ({ history: pushHistory(state.history, update(state.history.present)) }));
    },

    preview(update) {
      set((state) => ({ history: replacePresent(state.history, update(state.history.present)) }));
    },

    commit(base) {
      set((state) => ({ history: commitFrom(state.history, base) }));
    },

    undo() {
      set((state) => ({ history: undo(state.history) }));
    },

    redo() {
      set((state) => ({ history: redo(state.history) }));
    },

    setPlayhead(time) {
      set({ playhead: time });
    },

    startCut(time) {
      set({ cutMark: time });
    },

    finishCut(time) {
      const { cutMark } = get();
      if (cutMark === null) return;
      set({ cutMark: null });
      const cut = manualCut(cutMark, time, rowOf(get()).duration);
      if (cut) get().edit((recipe) => ({ ...recipe, cuts: addCut(recipe.cuts, cut) }));
    },

    cancelCut() {
      set({ cutMark: null });
    },

    startSpeed(time) {
      set({ speedMark: time });
    },

    finishSpeed(time) {
      const { speedMark, speedChoice } = get();
      if (speedMark === null) return;
      set({ speedMark: null });
      const span = range(Math.min(speedMark, time), Math.min(rowOf(get()).duration, Math.max(speedMark, time)));
      if (span.end - span.start >= MIN_SPEED_SEC) get().setSpeed(span, speedChoice);
    },

    cancelSpeed() {
      set({ speedMark: null });
    },

    setSpeedChoice(rate) {
      set({ speedChoice: rate });
    },

    setSpeed(span, rate) {
      get().edit((recipe) => ({ ...recipe, speeds: setSpeed(recipe.speeds, span, rate) }));
    },

    select(selection) {
      set({ selection });
    },

    setTimelineZoom(timelineZoom) {
      set({ timelineZoom });
    },

    async importFiles(files) {
      const token = session;
      const accepted = byName(files).flatMap((file) => {
        const kind = assetKindOf(file);
        return kind ? [{ file, kind }] : [];
      });
      const refused = files.filter((file) => assetKindOf(file) === null).map((file) => ({ name: file.name, error: new EngineError('unsupported-format', file.name) }));
      set((state) => ({ importing: { busy: state.importing.busy + accepted.length, failed: [...state.importing.failed, ...refused] } }));
      const added: AssetId[] = [];
      for (const { file, kind } of accepted) {
        try {
          const read = await readAsset(file, kind);
          if (token !== session) return added;
          const asset: Asset = { ...read, id: nextAssetId(), url: URL.createObjectURL(file), removed: false };
          set((state) => ({ assets: { ...state.assets, [asset.id]: asset }, assetOrder: [...state.assetOrder, asset.id] }));
          // Sounds are analyzed right away (their waveform shows on the timeline); videos once they join the edit.
          if (asset.kind === 'audio') analyzeAsset(asset, token);
          if (asset.kind === 'video') loadFilmstrip(asset, token);
          added.push(asset.id);
        } catch (error) {
          if (token !== session) return added;
          set((state) => ({ importing: { ...state.importing, failed: [...state.importing.failed, { name: file.name, error: isCancelled(error) ? null : toError(error) }] } }));
        } finally {
          if (token === session) set((state) => ({ importing: { ...state.importing, busy: Math.max(0, state.importing.busy - 1) } }));
        }
      }
      return added;
    },

    async importAndPlace(files, placement) {
      const added = await get().importFiles(files);
      const state = get();
      const clips = added.filter((id) => state.assets[id]?.kind === 'video');
      if (placement.as === 'clip' && clips.length > 0) {
        const row = rowOf(state);
        const fresh = clips.map((id) => wholeClip(id, state.assets[id]!.durationSec, nextItemId('clip')));
        const at = placement.at === 'end' ? row.duration : placement.at;
        arrangeRow(insertClipsAt(row, at, fresh, nextItemId('clip')));
        set({ selection: { kind: 'clip', id: fresh[0]!.id } });
      }
      // Pictures and sounds always go on top; videos too when that was asked for.
      const layered = placement.as === 'layer' ? added : added.filter((id) => state.assets[id]?.kind !== 'video');
      const at = placement.at === 'end' ? 0 : placement.at;
      for (const id of layered) get().place(id, { as: 'layer', at });
    },

    place(assetId, placement) {
      const state = get();
      const asset = state.assets[assetId];
      if (!asset) return;
      const row = rowOf(state);
      if (placement.as === 'clip') {
        if (asset.kind !== 'video') return;
        const clip = wholeClip(assetId, asset.durationSec, nextItemId('clip'));
        const at = placement.at === 'end' ? row.duration : placement.at;
        arrangeRow(insertClipsAt(row, at, [clip], nextItemId('clip')));
        set({ selection: { kind: 'clip', id: clip.id } });
        return;
      }
      const layer = createLayer(asset.kind, assetId, nextItemId('layer'), { at: placement.at, rowDuration: row.duration, assetDuration: asset.durationSec });
      state.edit((recipe) => ({ ...recipe, layers: [...recipe.layers, layer] }));
      set({ selection: { kind: 'layer', id: layer.id } });
    },

    appendUnused() {
      const state = get();
      const used = new Set(rowOf(state).clips.map((clip) => clip.assetId));
      const unused = state.assetOrder.map((id) => state.assets[id]).filter((asset): asset is Asset => Boolean(asset && asset.kind === 'video' && !asset.removed && !used.has(asset.id)));
      if (unused.length === 0) return;
      const row = rowOf(state);
      const fresh = unused.map((asset) => wholeClip(asset.id, asset.durationSec, nextItemId('clip')));
      arrangeRow([...row.clips.map(({ id, assetId, in: start, out, fit }) => ({ id, assetId, in: start, out, fit })), ...fresh]);
    },

    removeAsset(assetId) {
      const state = get();
      const recipe = state.history.present;
      const used = rowOf(state).clips.some((clip) => clip.assetId === assetId) || recipe.layers.some((layer) => layerAssetId(layer) === assetId);
      const asset = state.assets[assetId];
      if (!asset || used || assetId === PRIMARY_ASSET) return;
      // Kept (hidden) rather than dropped: an undo may still bring back a clip or layer made from it.
      set({ assets: { ...state.assets, [assetId]: { ...asset, removed: true } } });
    },

    dismissImportErrors() {
      set((state) => ({ importing: { ...state.importing, failed: [] } }));
    },

    splitAt(time) {
      const state = get();
      const clips = splitClipAt(rowOf(state), time, nextItemId('clip'));
      if (!clips) return false;
      arrangeRow(clips);
      return true;
    },

    moveClip(id, index) {
      arrangeRow(moveClipIn(currentClips(), id, index));
    },

    removeClip(id) {
      const clips = currentClips();
      if (clips.length <= 1) return;
      arrangeRow(removeClipFrom(clips, id));
      if (get().selection?.id === id) set({ selection: null });
    },

    setClipFit(id, fit) {
      arrangeRow(updateClip(currentClips(), id, { fit }));
    },

    updateLayer(id, update) {
      get().edit((recipe) => ({ ...recipe, layers: recipe.layers.map((layer) => (layer.id === id ? update(layer) : layer)) }));
    },

    removeLayer(id) {
      get().edit((recipe) => ({ ...recipe, layers: recipe.layers.filter((layer) => layer.id !== id) }));
      if (get().selection?.id === id) set({ selection: null });
    },

    restackLayer(id, direction) {
      get().edit((recipe) => {
        const layers = restack(recipe.layers, id, direction);
        return layers === recipe.layers ? recipe : { ...recipe, layers };
      });
    },

    moveLayerTo(id, index) {
      get().edit((recipe) => {
        const layers = moveInStack(recipe.layers, id, index);
        return layers === recipe.layers ? recipe : { ...recipe, layers };
      });
    },

    duplicateLayer(id) {
      const recipe = get().history.present;
      const index = recipe.layers.findIndex((layer) => layer.id === id);
      if (index === -1) return;
      const copy = duplicateLayer(recipe.layers[index]!, nextItemId('layer'));
      get().edit((current) => ({ ...current, layers: [...current.layers.slice(0, index + 1), copy, ...current.layers.slice(index + 1)] }));
      set({ selection: { kind: 'layer', id: copy.id } });
    },

    addTextLayer(text) {
      const state = get();
      const row = rowOf(state);
      if (!state.media || !(row.duration > 0)) return;
      const recipe = state.history.present;
      // Set at a caption-like size on the frame being made, whatever its shape.
      const { output } = selectLayout(state.media, recipe.format, selectCamera(recipe, state.camera !== null));
      const size = textSizeFor(layoutText(text, DEFAULT_TEXT_STYLE, measureText), output);
      const layer = createTextLayer(nextItemId('layer'), { at: state.playhead, rowDuration: row.duration, text, size });
      state.edit((recipe) => ({ ...recipe, layers: [...recipe.layers, layer] }));
      set({ selection: { kind: 'layer', id: layer.id }, freshText: layer.id });
    },

    editText(id) {
      set({ selection: { kind: 'layer', id }, freshText: id });
    },

    clearFreshText() {
      if (get().freshText !== null) set({ freshText: null });
    },

    setScanOptions(options) {
      if (options.precision) writePreference('privacy-precision', options.precision);
      set((state) => ({ privacy: { ...state.privacy, ...options } }));
    },

    scanPrivacy({ kinds = SUGGESTED_SCAN_KINDS, revealAll = false, assets, mode = 'replace' } = {}) {
      const state = get();
      if (!state.file) return;
      const targets = assets ?? rowAssets(state).map((asset) => asset.id);
      for (const kind of kinds) {
        const fresh: AssetId[] = [];
        for (const assetId of targets) {
          const queued = scanQueue.findIndex((job) => job.kind === kind && job.assetId === assetId);
          // A waiting job decides what to look at when it starts; asked for a full rescan, it becomes one.
          if (queued !== -1) {
            if (mode === 'replace') scanQueue[queued] = { ...scanQueue[queued]!, mode, revealAll };
            continue;
          }
          // The one running may have started before the edit changed, so more parts can still be queued behind it.
          if (mode === 'replace' && scanning?.kind === kind && scanning.assetId === assetId) continue;
          fresh.push(assetId);
        }
        if (fresh.length === 0) continue;
        scanQueue.push(...fresh.map((assetId) => ({ kind, assetId, revealAll, mode })));
        scanRounds[kind].total += fresh.length;
        if (!isScanBusy(get().privacy.scans[kind])) setScan(kind, (scan) => ({ ...scan, status: 'queued', progress: null, error: null, partial: mode === 'extend' }));
      }
      void runNextScan(session);
    },

    scanUnscanned() {
      cancelCoverageCheck();
      const state = get();
      if (!state.file || state.load.status !== 'ready') return;
      // Only kinds the person chose to look for; a first scan still running checks again when it ends.
      const kinds = DETECTION_KINDS.filter((kind) => state.privacy.scans[kind].completed);
      if (kinds.length === 0) return;
      const wantedOf = wantedParts(state);
      const videos = rowAssets(state).map((asset) => asset.id);
      for (const kind of kinds) {
        const missing = unscannedAssets(state.privacy.covered, kind, videos, wantedOf);
        if (missing.length > 0) get().scanPrivacy({ kinds: [kind], assets: missing, mode: 'extend' });
      }
    },

    cancelScan(kind) {
      scanQueue = scanQueue.filter((job) => job.kind !== kind);
      scanRounds[kind] = freshRound();
      if (scanning?.kind === kind) return void tasks.get('scan')?.cancel();
      setScan(kind, settledScan);
    },

    clearScan(kind) {
      const { privacy } = get();
      if (isScanBusy(privacy.scans[kind])) return;
      const removed = Object.values(privacy.tracksByAsset)
        .flat()
        .filter((item) => item.kind === kind)
        .map((item) => item.id);
      for (const id of removed) if (privacy.thumbnails[id]) URL.revokeObjectURL(privacy.thumbnails[id]);
      set((state) => ({
        privacy: {
          ...state.privacy,
          scans: { ...state.privacy.scans, [kind]: IDLE_SCAN },
          covered: { ...state.privacy.covered, [kind]: {} },
          tracksByAsset: Object.fromEntries(Object.entries(state.privacy.tracksByAsset).map(([id, list]) => [id, replaceKindTracks(list, kind, [])])),
          thumbnails: Object.fromEntries(Object.entries(state.privacy.thumbnails).filter(([id]) => !removed.includes(id))),
        },
        history: replacePresent(state.history, { ...state.history.present, privacy: resetKindReveals(state.history.present.privacy, kind) }),
      }));
    },

    setDrawingArea(drawing) {
      set((state) => ({ areas: { ...state.areas, drawing } }));
    },

    async addArea(request) {
      const state = get();
      if (!state.file || state.areas.status === 'running') return;
      // The area was drawn on the frame at a row moment: follow it in the video showing then.
      const located = assetTimeAt(rowOf(state), request.time);
      const asset = located && state.assets[located.clip.assetId];
      if (!located || !asset) return;
      const token = session;
      set({ areas: { ...state.areas, drawing: false, status: 'running', progress: { phase: 'scan', ratio: 0 }, error: null } });
      const task = track(
        'area',
        engine.trackRegion(asset.file, { time: located.time, box: unplaceBox(request.box, located.clip.rect) }, (progress) => {
          if (token === session) set((current) => ({ areas: { ...current.areas, progress } }));
        }),
      );
      try {
        const { track: found, thumbnail } = await task.promise;
        if (token !== session) return;
        const manual = asset.id === PRIMARY_ASSET ? found : { ...found, assetId: asset.id };
        set((current) => ({
          areas: { ...current.areas, status: 'idle', progress: null, thumbnails: { ...current.areas.thumbnails, [found.id]: URL.createObjectURL(thumbnail) } },
          history: pushHistory(current.history, { ...current.history.present, privacy: addManualTrack(current.history.present.privacy, manual) }),
        }));
      } catch (error) {
        if (token !== session) return;
        set((current) => ({
          areas: isCancelled(error)
            ? { ...current.areas, status: 'idle', progress: null }
            : { ...current.areas, status: 'error', progress: null, error: toError(error) },
        }));
      }
    },

    cancelAreaTracking() {
      tasks.get('area')?.cancel();
    },

    removeArea(trackId) {
      // The thumbnail is kept so that undo can bring the area back with its picture.
      get().edit((recipe) => ({ ...recipe, privacy: removeManualTrack(recipe.privacy, trackId) }));
    },

    setTranscriptionOptions(options) {
      if (options.quality) writePreference('caption-quality', options.quality);
      if (options.language) writePreference('caption-language', options.language);
      set((state) => ({ transcription: { ...state.transcription, ...options } }));
    },

    async transcribe({ onlyMissing = false, burnIn = true } = {}) {
      const state = get();
      const { transcription } = state;
      if (!state.file || transcription.status === 'running') return;
      const targets = rowAssets(state).filter((asset) => asset.hasAudio && (!onlyMissing || !transcription.captioned.includes(asset.id)));
      if (targets.length === 0) return;
      const token = session;
      set({ transcription: { ...transcription, status: 'running', progress: { phase: 'model', ratio: 0 }, error: null } });
      // Clips added later are written the way the captions already are.
      const task: CaptionTask = onlyMissing ? (state.history.present.captions.translatedFrom ? 'translate' : 'transcribe') : transcription.task;
      const options = { quality: transcription.quality, language: transcription.language, task, fallbackLanguage: fallbackSpeechLanguage(navigator.languages) };
      const heard: { readonly assetId: AssetId; readonly words: readonly CaptionWord[] }[] = [];
      let spoken: string | null = null;
      try {
        for (const [index, asset] of targets.entries()) {
          const task = track(
            'transcribe',
            engine.transcribe(asset.file, options, (progress) => {
              if (token !== session) return;
              // Several videos share one bar; the model download shows on its own the first time.
              const overall = progress.phase === 'model' ? progress : { ...progress, ratio: (index + progress.ratio) / targets.length };
              set((current) => ({ transcription: { ...current.transcription, progress: overall } }));
            }),
          );
          const result = await task.promise;
          if (token !== session) return;
          spoken ??= result.language;
          heard.push({ assetId: asset.id, words: result.words });
        }
        set((current) => {
          // Placed with the row as it is now, in case clips moved while it ran.
          const row = rowOf(current);
          const words = heard.flatMap(({ assetId, words: list }) => placeTimed(row, assetId, list)).sort((a, b) => a.start - b.start);
          const fresh = wordsToCues(words);
          const present = current.history.present;
          const stamp = Date.now().toString(36);
          const cues = onlyMissing ? [...present.captions.cues, ...fresh.map((cue) => ({ ...cue, id: `${cue.id}-${stamp}` }))].sort((a, b) => a.start - b.start) : fresh;
          const captioned = heard.map((entry) => entry.assetId);
          // A translation records what was spoken, so the words aren't taken for speech (fillers, the script).
          const translatedFrom = onlyMissing ? present.captions.translatedFrom : spoken && whisperTask(task, spoken) === 'translate' ? spoken : undefined;
          return {
            transcription: {
              ...current.transcription,
              status: 'idle',
              progress: null,
              completed: true,
              suggested: false,
              spoken: current.transcription.spoken && onlyMissing ? current.transcription.spoken : spoken,
              captioned: onlyMissing ? [...new Set([...current.transcription.captioned, ...captioned])] : captioned,
            },
            history: pushHistory(current.history, { ...present, captions: { ...present.captions, enabled: burnIn || present.captions.enabled, cues, translatedFrom } }),
          };
        });
      } catch (error) {
        if (token !== session) return;
        set((current) => ({
          transcription: isCancelled(error)
            ? { ...current.transcription, status: 'idle', progress: null }
            : { ...current.transcription, status: 'error', progress: null, error: toError(error) },
        }));
      }
    },

    cancelTranscription() {
      tasks.get('transcribe')?.cancel();
    },

    setCameraBackdrop(file) {
      const previous = get().cameraBackdrop;
      if (previous) URL.revokeObjectURL(previous.url);
      set({ cameraBackdrop: file && { file, url: URL.createObjectURL(file) } });
    },

    async analyzeScreen() {
      const { file, screen } = get();
      if (!file || screen.status === 'running' || screen.status === 'done') return;
      const token = session;
      set({ screen: { status: 'running', progress: { phase: 'scan', ratio: 0 }, activity: null, notifications: [] } });
      const task = track(
        'screen',
        engine.analyzeScreen(file, (progress) => {
          if (token === session) set((state) => ({ screen: { ...state.screen, progress } }));
        }),
      );
      try {
        const result = await task.promise;
        if (token !== session) return;
        const thumbnails = Object.fromEntries(Object.entries(result.thumbnails).map(([id, blob]) => [id, URL.createObjectURL(blob)]));
        set((state) => {
          const present = state.history.present;
          const known = new Set(present.privacy.manualTracks.map((item) => item.id));
          const found = notificationTracks(result.notifications).filter((item) => !known.has(item.id));
          return {
            screen: { status: 'done', progress: null, activity: result.activity, notifications: result.notifications },
            areas: { ...state.areas, thumbnails: { ...state.areas.thumbnails, ...thumbnails } },
            // Privacy first: notifications are hidden until revealed. Not an undo step; it is what was found.
            history:
              found.length > 0
                ? replacePresent(state.history, { ...present, privacy: { ...present.privacy, enabled: true, manualTracks: [...present.privacy.manualTracks, ...found] } })
                : state.history,
          };
        });
      } catch (error) {
        if (token !== session) return;
        set({ screen: { status: isCancelled(error) ? 'idle' : 'error', progress: null, activity: null, notifications: [] } });
      }
    },

    setUnits(units) {
      rememberUnits(units);
      get().edit((recipe) => ({ ...recipe, telemetry: { ...recipe.telemetry, units } }));
    },

    applyPreset(preset, { initial = false } = {}) {
      const { settings } = preset;
      const state = get();
      if (initial) set((current) => ({ history: createHistory(applyPreset(current.history.present, settings)) }));
      else state.edit((recipe) => applyPreset(recipe, settings));
      if (settings.privacy.scan.length > 0) {
        state.setScanOptions({ precision: settings.privacy.precision });
        // The detection models are small (a few MB, disclosed where the recipe is chosen), so the scans start straight away.
        const scans = get().privacy.scans;
        const wanted = settings.privacy.scan.filter((kind) => !scans[kind].completed && !isScanBusy(scans[kind]));
        if (wanted.length > 0) get().scanPrivacy({ kinds: wanted });
      }
      // A recipe with auto zoom needs to know where the screen changes (and finds notifications on the way).
      if (settings.zoom.enabled && state.media) void get().analyzeScreen();
      if (settings.captions.auto && get().history.present.captions.cues.length === 0) {
        state.setTranscriptionOptions({ language: settings.captions.language, quality: settings.captions.quality });
        // A recipe that doesn't burn captions in wants them for the SRT file only.
        void autoTranscribe(session, { burnIn: settings.captions.enabled });
      }
    },

    currentPresetSettings() {
      const { history, privacy, transcription } = get();
      const scan = scannedKinds(privacy.scans);
      return presetFromRecipe(history.present, {
        scan,
        precision: privacy.precision,
        captionLanguage: transcription.language,
        captionQuality: transcription.quality,
        hasCaptions: history.present.captions.cues.length > 0,
        scanned: scan.length > 0,
      });
    },

    setExportName(name) {
      set({ exportName: name });
    },

    openExport() {
      set((state) => ({ exporting: { ...idleExport, status: 'configuring', ...keptResults(state.exporting) } }));
    },

    async startExport(job, options) {
      await get().startExports([{ job, name: options?.name }]);
    },

    async startExports(items) {
      const { file, exporting, media } = get();
      if (!file || !media || exporting.status === 'running' || items.length === 0) return;
      for (const old of exporting.results) URL.revokeObjectURL(old.url);
      const token = session;
      const results: SavedResult[] = [];
      const batchOf = (index: number, item: ExportItem): ExportBatch | null => {
        if (items.length < 2) return null;
        const output = selectLayout(media, item.job.recipe.format, selectCamera(item.job.recipe, get().camera !== null)).output;
        return { index, total: items.length, extra: item.extra ?? null, shape: output.width / Math.max(1, output.height) };
      };
      set({ exporting: { ...idleExport, status: 'running', progress: { phase: 'encode', ratio: 0 }, startedAt: performance.now(), batch: batchOf(0, items[0]!) } });
      const finish = (error: EngineError | null) =>
        set((state) => ({
          exporting: { ...idleExport, status: 'done', result: results[0] ?? null, results, error },
          recording: state.recording && { exported: true },
        }));
      for (const [index, item] of items.entries()) {
        if (index > 0) set((state) => ({ exporting: { ...state.exporting, progress: { phase: 'encode', ratio: 0 }, startedAt: performance.now(), batch: batchOf(index, item) } }));
        try {
          const result = await exportOne(item, token);
          if (token !== session) return void URL.revokeObjectURL(result.url);
          results.push(result);
        } catch (error) {
          if (token !== session) return;
          // Whatever was made before stays ready to save; only with nothing made does it go back (or fail).
          if (results.length > 0) {
            finish(isCancelled(error) ? null : toError(error));
            origin?.exported();
          } else set({ exporting: isCancelled(error) ? { ...idleExport, status: 'configuring' } : { ...idleExport, status: 'error', error: toError(error) } });
          return;
        }
      }
      finish(null);
      origin?.exported();
    },

    cancelExport() {
      tasks.get('export')?.cancel();
    },

    closeExport() {
      const { exporting } = get();
      if (exporting.status === 'running') return;
      set({ exporting: { ...idleExport, ...keptResults(exporting) } });
    },
  };
});

/** Current recipe (shorthand). */
export const useRecipe = () => useEditorStore((state) => state.history.present);

/**
 * True when leaving would throw away something the user would miss: a job
 * they started, and — when the edit couldn't be saved as a project on this
 * device — their own edits, captions, drawn areas, imported media or a
 * recording that was never exported.
 */
export function selectHasWork(state: EditorStore): boolean {
  const recipe = state.history.present;
  const running = state.transcription.status === 'running' || state.areas.status === 'running' || state.exporting.status === 'running';
  if (state.projectSaved) return running;
  return (
    (state.recording !== null && !state.recording.exported) ||
    state.history.past.length > 0 ||
    recipe.captions.cues.length > 0 ||
    recipe.privacy.manualTracks.length > 0 ||
    state.assetOrder.length > 1 ||
    state.transcription.status === 'running' ||
    state.areas.status === 'running' ||
    state.exporting.status === 'running'
  );
}

/** Looks for parts the edit shows that no scan has seen, once editing has paused (see `scanUnscanned`). */
function scheduleCoverageCheck(): void {
  cancelCoverageCheck();
  coverageTimer = setTimeout(() => {
    coverageTimer = null;
    useEditorStore.getState().scanUnscanned();
  }, COVERAGE_CHECK_DELAY_MS);
}

// Undoing a cut, making a clip longer or turning off a removal brings parts back into the edit.
useEditorStore.subscribe((current, previous) => {
  if (current.load.status !== 'ready' || !DETECTION_KINDS.some((kind) => current.privacy.scans[kind].completed)) return;
  if (current.history.present !== previous.history.present || current.analyses !== previous.analyses || current.telemetry !== previous.telemetry) scheduleCoverageCheck();
});
